import { SubmitHandler, UseFormReturn } from "react-hook-form";

import {
  IconAlignBoxLeftTopFilled,
  IconEdit,
  IconPhotoCirclePlus,
  IconTypography,
  IconX,
} from "@tabler/icons-react";
import { ReactNode } from "react";
import { ErrorMessage } from "@hookform/error-message";
import TextArea from "../../core/TextArea.tsx";
import {
  INVALID_USERNAME,
  REQUIERED,
  TOO_LONG,
  TOO_SHORT,
} from "../../../const/ValidationHints.ts";
import Input from "../../core/Input.tsx";
import {
  DESCRIPTION_VALIDATION,
  USERNAME_REGEX,
} from "../../../const/Pattern.ts";
import { FileType } from "../../../types/File";
import { SubmitStateType } from "../../../hooks/useSubmitState";
import { LOADING_CIRCLE } from "../../core/loading/LoadingCircle.tsx";
import ImageInput from "../../item/input/ImageInput.tsx";
import Image from "../../core/image/Image.tsx";
import { UserType } from "../../../types/User.ts";
import { ERROR } from "../../../types/Error/Errors.ts";
import ImageDelete from "../../item/input/ImageDelete.tsx";
import ImageUpload from "../../item/input/ImageUpload.tsx";

interface UserFormProps {
  children?: ReactNode;
  onSubmit: (data: UserType) => Promise<void>;
  userForm: UseFormReturn<UserType>;
  submitState: SubmitStateType;
}

const loadingButton = (
  <>
    {LOADING_CIRCLE}
    Profilbild hochladen
  </>
);

export function UserForm({
  children,
  onSubmit,
  userForm,
  submitState,
}: UserFormProps) {
  const { handleSubmit, register, getValues, setValue, watch, formState } =
    userForm;
  const { errors, touchedFields } = formState;

  // Handle form submission
  const submit: SubmitHandler<UserType> = async (_data) => {
    submitState.init(loadingButton);
    const waitForUploads = () =>
      new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          clearInterval(interval);
          reject(new Error("Upload timed out"));
        }, 30_000);

        const interval = setInterval(() => {
          const file = getValues("avatarUrl") as FileType;
          if (!file || !file.local) {
            clearInterval(interval);
            clearTimeout(timeout);
            resolve();
          }
        }, 500);
      });
    try {
      await waitForUploads();
    } catch {
      submitState.fetchFailed(ERROR.UPLOAD_TIMEOUT, ERROR.UPLOAD_TIMEOUT);
      return;
    }

    await onSubmit({
      ...getValues(),
    });
  };
  const addFile = (files: FileType[]) => {
    if (files.length < 0) return;
    setValue("avatarUrl", files[0]);
  };
  const avatarUrl = watch("avatarUrl");

  return (
    <form
      onSubmit={handleSubmit(submit)}
      className="flex flex-col gap-4 w-full p-4"
    >
      <div className="flex lg:flex-row flex-col items-center p-3 shadow-xl w-full">
        {!avatarUrl && (
          <ImageInput onFileChange={addFile}>
            {(handleClick) => (
              <button
                onClick={handleClick}
                type="button"
                className="btn w-32 h-32 aspect-square object-scale-down rounded-full"
              >
                <IconPhotoCirclePlus className="w-full" />
              </button>
            )}
          </ImageInput>
        )}

        {avatarUrl && (
          <div className="relative">
            <Image
              alt={"Upload"}
              src={avatarUrl}
              sizes="64px"
              className="w-32 h-32 aspect-square object-scale-down rounded-full"
            />
            <ImageDelete
              context={"profile"}
              contextId={getValues("_id")}
              file={avatarUrl}
              onFileDelete={() => setValue("avatarUrl", undefined)}
            >
              {(handleClick) => (
                <button
                  type="button"
                  onClick={handleClick}
                  className="btn absolute inset-0 w-32 h-32 hover:opacity-100 bg-base-100/40 opacity-80 rounded-full"
                >
                  <IconX className="w-full" />
                </button>
              )}
            </ImageDelete>
            <ImageInput onFileChange={addFile}>
              {(handleClick) => (
                <button
                  onClick={handleClick}
                  type="button"
                  className="btn btn-info btn-circle absolute bottom-4 right-0"
                >
                  <IconEdit />
                </button>
              )}
            </ImageInput>

            <ImageUpload
              file={avatarUrl}
              context="profile"
              contextId={getValues("_id")}
              onUpload={(file) => setValue("avatarUrl", file)}
              onError={(_file) => setValue("avatarUrl", undefined)}
            >
              {(local) =>
                local ? (
                  <div
                    className={`absolute btn-circle top-2 right-2 badge ${local ? "badge-info" : "badge-success"}`}
                  >
                    {LOADING_CIRCLE}
                  </div>
                ) : null
              }
            </ImageUpload>
          </div>
        )}

        <div className="card-body">
          <Input
            input="input"
            validatorHint={<ErrorMessage errors={errors} name="username" />}
            icon={<IconTypography stroke={2} />}
            label={"Dein Nutzername"}
            isTouched={!!touchedFields.username}
            isError={!!errors.username}
          >
            <input
              type="text"
              placeholder={`Gib dir einen Anzeigenamen`}
              {...register("username", {
                required: REQUIERED,
                minLength: { value: 3, message: TOO_SHORT(3) },
                maxLength: { value: 20, message: TOO_LONG(20) },
                pattern: { value: USERNAME_REGEX, message: INVALID_USERNAME },
              })}
            />
          </Input>
          <Input
            input="textarea"
            validatorHint={<ErrorMessage errors={errors} name="description" />}
            icon={<IconAlignBoxLeftTopFilled stroke={2} />}
            label="Beschreibung"
            isTouched={!!touchedFields.description}
            isError={!!errors.description}
          >
            <TextArea
              placeholder="Wenn du magst, schreibe etwas kurzes über dich..."
              fieldKey={"description"}
              register={register}
              registerOptions={DESCRIPTION_VALIDATION}
            />
          </Input>
          <div>{children}</div>
        </div>
      </div>
    </form>
  );
}

export default UserForm;
